import React, { useState, useEffect } from "react";
import "./BreezeProCard.css";
import { Accordion, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faWind,
    faCode,
    faBatteryFull,
    faBatteryThreeQuarters,
    faBatteryHalf,
    faBatteryQuarter,
    faBatteryEmpty
} from "@fortawesome/free-solid-svg-icons";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import { tagColor, getBondColor } from "../../../../../../lib/ui/colorUtils";
import { padDeviceId } from "../../../../../../lib/utils";
import ReactJson from "react-json-view";

function BreezeProCard({ breezePros, bondsData }) {
    const { formatDate, getRelativeTime } = useTimezone();
    const [renderedBreezePros, setRenderedBreezePros] = useState([]);

    useEffect(() => {
        renderBreezePros();
    }, [breezePros, bondsData, formatDate]);

    const getDeviceInfo = (deviceId, bondId) => {
        if (!bondsData || !bondId) return null;

        const bondData = bondsData[bondId];
        if (!bondData?.devices) return null;

        const devicesList = Array.isArray(bondData.devices)
            ? bondData.devices
            : Object.values(bondData.devices);

        // Pad both IDs for comparison
        const paddedSearchId = padDeviceId(deviceId);
        return devicesList.find(d => {
            const dId = d.device_id;
            const paddedDId = padDeviceId(dId);
            return dId === deviceId || dId === paddedSearchId || paddedDId === deviceId || paddedDId === paddedSearchId;
        });
    };

    const renderBreezePros = () => {
        if (!breezePros || breezePros.length === 0) {
            setRenderedBreezePros([]);
            return;
        }

        const rendered = breezePros.map((breezePro, index) => {
            const data = breezePro.data || {};
            return (
                <Accordion.Item eventKey={`breezepro-${index}`} key={breezePro.sidekick_id || index}>
                    <Accordion.Header>
                        <div className="d-flex align-items-center w-100" style={{ gap: '0.5rem' }}>
                            {getBatteryBadge(data.battery)}
                            <span className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                {data.name || breezePro.sidekick_id}
                            </span>
                            <div className="d-flex flex-shrink-0" style={{ gap: '0.25rem' }}>
                                {breezePro.bond_ids && breezePro.bond_ids.map((bondId, idx) => (
                                    <Badge key={idx} className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>
                                        {bondId}
                                    </Badge>
                                ))}
                                {data.model && (
                                    <Badge bg="dark">{data.model}</Badge>
                                )}
                            </div>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-3">
                        {/* Name - Bond Id */}
                        <div className="d-flex gap-2 mb-2">
                            {data.name && (
                                <FloatingLabel label="Name" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.name} />
                                </FloatingLabel>
                            )}
                            {breezePro.sidekick_id && (
                                <FloatingLabel label="Bond ID" className="flex-fill">
                                    <Form.Control readOnly={true} value={breezePro.sidekick_id} />
                                </FloatingLabel>
                            )}
                        </div>

                        {/* Model - Fw version */}
                        <div className="d-flex gap-2 mb-2">
                            {data.model && (
                                <FloatingLabel label="Model" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.model} />
                                </FloatingLabel>
                            )}
                            {data.fw_ver && (
                                <FloatingLabel label="Firmware Version" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.fw_ver} />
                                </FloatingLabel>
                            )}
                        </div>

                        {/* Location - Type */}
                        <div className="d-flex gap-2 mb-2">
                            {data.location && (
                                <FloatingLabel label="Location" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.location} />
                                </FloatingLabel>
                            )}
                            {data.type && (
                                <FloatingLabel label="Type" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.type} />
                                </FloatingLabel>
                            )}
                        </div>

                        {/* Battery - Signal */}
                        <div className="d-flex gap-2 mb-2">
                            {data.battery !== undefined && (
                                <FloatingLabel label="Battery" className="flex-fill">
                                    <Form.Control readOnly={true} value={`${data.battery}%`} />
                                </FloatingLabel>
                            )}
                            {data.signal !== undefined && (
                                <FloatingLabel label="Signal" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.signal} />
                                </FloatingLabel>
                            )}
                        </div>

                        {/* Weather Event Mappings */}
                        {data.event_map && data.event_links && Object.keys(data.event_map).length > 0 && (
                            <div className="mb-3">
                                <h6>Weather Events</h6>
                                <ListGroup>
                                    {Object.entries(data.event_map).map(([eventKey, eventName]) => {
                                        const links = data.event_links[eventKey] || [];
                                        if (links.length === 0) return null;

                                        return links.map((link, idx) => {
                                            const bondId = breezePro.bond_ids?.[0] || breezePro.bond_id;
                                            const device = getDeviceInfo(link.device, bondId);
                                            const deviceName = device?.name || link.device;

                                            return (
                                                <ListGroup.Item key={`${eventKey}-${idx}`}>
                                                    <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                                                        <Badge bg="success" className="flex-shrink-0" style={{textTransform: 'capitalize', minWidth: '80px'}}>
                                                            {eventName.replace('_', ' ')}
                                                        </Badge>
                                                        <Badge
                                                            className="bondBadge flex-shrink-0"
                                                            style={{'--bond-badge-bg': getBondColor(bondId)}}
                                                        >
                                                            {bondId}
                                                        </Badge>
                                                        <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                            {deviceName}
                                                        </span>
                                                        <div className="d-flex flex-shrink-0">
                                                            <Badge bg="secondary">{link.action}</Badge>
                                                        </div>
                                                    </div>
                                                </ListGroup.Item>
                                            );
                                        });
                                    })}
                                </ListGroup>
                            </div>
                        )}

                        {/* Created - Modified */}
                        {breezePro.created && (
                            <div className="d-flex gap-2 mb-2">
                                <FloatingLabel label={`Created ${getRelativeTime(breezePro.created)}`} className="flex-fill">
                                    <Form.Control readOnly={true} value={formatDate(breezePro.created)} />
                                </FloatingLabel>
                                {breezePro.modified && (
                                    <FloatingLabel label={`Modified ${getRelativeTime(breezePro.modified)}`} className="flex-fill">
                                        <Form.Control readOnly={true} value={formatDate(breezePro.modified)} />
                                    </FloatingLabel>
                                )}
                            </div>
                        )}

                        {/* Bridges */}
                        {breezePro.bond_ids && breezePro.bond_ids.length > 0 && (
                            <div className="mb-3">
                                <h6>Bridges</h6>
                                <div className="d-flex flex-wrap gap-1">
                                    {breezePro.bond_ids.map((bondId, idx) => (
                                        <Badge key={idx} className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>
                                            {bondId}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Raw JSON */}
                        <div className="mt-3">
                            <Accordion>
                                <Accordion.Item eventKey="json">
                                    <Accordion.Header>
                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                        Raw JSON
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <ReactJson
                                            src={breezePro.raw_sidekicks || [breezePro]}
                                            theme="ocean"
                                            collapsed={1}
                                            displayDataTypes={false}
                                            displayObjectSize={false}
                                            enableClipboard={true}
                                            name={false}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            );
        });

        setRenderedBreezePros(rendered);
    };

    const getBatteryBadge = (battery) => {
        if (battery === undefined) return null;

        let icon, color;
        if (battery > 75) {
            icon = faBatteryFull;
            color = "success";
        } else if (battery > 50) {
            icon = faBatteryThreeQuarters;
            color = "success";
        } else if (battery > 25) {
            icon = faBatteryHalf;
            color = "warning";
        } else if (battery > 10) {
            icon = faBatteryQuarter;
            color = "danger";
        } else {
            icon = faBatteryEmpty;
            color = "danger";
        }

        return (
            <Badge bg={color} className="flex-shrink-0">
                <FontAwesomeIcon icon={icon} className="me-1" />
                {battery}%
            </Badge>
        );
    };

    if (!breezePros || breezePros.length === 0) {
        return null;
    }

    return (
        <Accordion defaultActiveKey="" className="mb-2">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <FontAwesomeIcon icon={faWind} className="me-2" />
                            Breeze Pro
                        </div>
                        <Badge bg="dark">
                            {breezePros.length}
                        </Badge>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="p-2">
                    <Accordion>
                        {renderedBreezePros}
                    </Accordion>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default BreezeProCard;
